import { Auth0ContextInterface } from "@auth0/auth0-react";
import MerchantDomainBase from "entities/domain/admin/merchants/merchant-domain";
import { MerchantUsageDomain } from "entities/domain/admin/merchants/merchant-usage";
import VehicleDomain, { VehicleMakeAndModel } from "entities/domain/vehicle";
import { WorkingHoursDayDomain } from "entities/domain/working-hours";
import { BankAccountType } from "entities/dto/MerchantDTO";
import { VehicleDTO } from "entities/dto/VehicleDTO";
import { VehicleMakeAndModelDTO } from "entities/dto/VehicleMakeAndModelDTO";
import { WorkingDay, WorkingHoursDTO } from "entities/dto/WorkingHoursDTO";
import {
  merchantTransformFromDtoToBaseDomain,
  merchantUsageTransformerDtoToDomain,
} from "entities/transformers/admin-merchant-transformer";
import {
  vehicleMakeAndModelTransformFromDtoToDomain,
  vehicleTransformFromDtoToDomain,
} from "entities/transformers/vehicleTransformer";
import { workingHoursTransformFromDtoToHoursDayDomain } from "entities/transformers/working-hours-transformer";
import { RequestType } from "services/request-type";
import {
  deleteRequest,
  getDayNameByIndex,
  patchRequest,
  postRequest,
  putRequest,
  request,
} from "util/methods";

export interface UpdateMerchantBankDetailsPayload {
  bank_account: BankAccountType;
}

export interface UpdateMerchantPayload {
  googlePlaceId?: string;
  logoUrl?: string;
}

export default class MerchantService {
  public static async getMerchant(
    { getAccessTokenSilently, user }: Auth0ContextInterface,
    id: number
  ): Promise<MerchantDomainBase> {
    const accessToken = await getAccessTokenSilently();

    const merchant = (
      await request(RequestType.GET, accessToken, `/merchants/${id}`)
    ).data;

    return merchantTransformFromDtoToBaseDomain(merchant);
  }

  public static async getMerchantVehicleMakesAndModels(
    { getAccessTokenSilently }: Auth0ContextInterface,
    groupId: string
  ): Promise<VehicleMakeAndModel[]> {
    const accessToken = await getAccessTokenSilently();

    const makesAndModels = (
      await request<VehicleMakeAndModelDTO[]>(
        RequestType.GET,
        accessToken,
        `/merchant-groups/${groupId}/vehicles/makes`
      )
    ).data;

    return makesAndModels.map(vehicleMakeAndModelTransformFromDtoToDomain);
  }

  /**
   * The sales-order-type values this group's vehicles actually hold.
   *
   * `sales_order_type` is an open set — whatever the group's DMS calls a
   * vehicle's commercial status — so pickers must offer real data rather than
   * a fixed list that would omit the values a dealer most needs to filter on.
   */
  public static async getMerchantVehicleSalesOrderTypes(
    { getAccessTokenSilently }: Auth0ContextInterface,
    groupId: string
  ): Promise<string[]> {
    const accessToken = await getAccessTokenSilently();

    const salesOrderTypes = (
      await request<{ name: string }[]>(
        RequestType.GET,
        accessToken,
        `/merchant-groups/${groupId}/vehicles/sales-order-types`
      )
    ).data;

    return salesOrderTypes.map(({ name }) => name);
  }

  public static async getVehicle(
    { getAccessTokenSilently }: Auth0ContextInterface,
    merchantGroupId: string,
    vehicleId: string
  ): Promise<VehicleDomain | null> {
    const accessToken = await getAccessTokenSilently();

    try {
      const vehicleResponse = (
        await request<VehicleDTO>(
          RequestType.GET,
          accessToken,
          `/merchant-groups/${merchantGroupId}/vehicles/${vehicleId}`
        )
      ).data;

      return vehicleTransformFromDtoToDomain(vehicleResponse);
    } catch (error: unknown) {
      return null;
    }
  }

  public static async updateWorkingHours(
    { getAccessTokenSilently }: Auth0ContextInterface,
    workingHours: Array<WorkingHoursDayDomain | null>,
    merchantId: number
  ): Promise<void> {
    const accessToken = await getAccessTokenSilently();

    const newWorkingHours = {} as WorkingHoursDTO;

    workingHours.forEach((workingHour, index) => {
      const dayName = getDayNameByIndex(index);

      if (workingHour) {
        newWorkingHours[dayName as WorkingDay] = {
          start_time: {
            hour: workingHour.open.hour,
            minute: workingHour.open.minute,
          },
          end_time: {
            hour: workingHour.close.hour,
            minute: workingHour.close.minute,
          },
        };
      }
    });

    await putRequest(
      RequestType.PUT,
      accessToken,
      `/merchants/${merchantId}/working-hours`,
      newWorkingHours
    );
  }

  public static async getWorkingHours(
    { getAccessTokenSilently }: Auth0ContextInterface,
    merchantId: number
  ): Promise<Array<WorkingHoursDayDomain | null>> {
    const accessToken = await getAccessTokenSilently();

    const workingHours = (
      await request<WorkingHoursDTO>(
        RequestType.GET,
        accessToken,
        `/merchants/${merchantId}/working-hours`
      )
    ).data;

    return workingHoursTransformFromDtoToHoursDayDomain(workingHours);
  }

  public static async updateMerchant(
    { getAccessTokenSilently }: Auth0ContextInterface,
    payload: UpdateMerchantPayload,
    merchantId: number
  ): Promise<MerchantDomainBase> {
    const accessToken = await getAccessTokenSilently();

    const merchant = (
      await patchRequest(
        RequestType.PATCH,
        accessToken,
        `/merchants/${merchantId}`,
        {
          google_place_id: payload.googlePlaceId,
          logo_url: payload.logoUrl,
        }
      )
    ).data;

    return merchantTransformFromDtoToBaseDomain(merchant);
  }

  public static async updateMerchantBankDetails(
    { getAccessTokenSilently }: Auth0ContextInterface,
    payload: UpdateMerchantBankDetailsPayload,
    merchantId: number
  ): Promise<void> {
    const accessToken = await getAccessTokenSilently();

    await putRequest(
      RequestType.PUT,
      accessToken,
      `/merchants/${merchantId}/bank-account`,
      payload.bank_account
    );
  }

  public static async getApiKey(
    { getAccessTokenSilently }: Auth0ContextInterface,
    merchantId: number
  ): Promise<string | null> {
    const accessToken = await getAccessTokenSilently();

    try {
      const response = await request<{ api_key: string }>(
        RequestType.GET,
        accessToken,
        `merchants/${merchantId}/api-key`
      );
      return response.data.api_key;
    } catch (err) {
      return null;
    }
  }

  public static async generateApiKey(
    { getAccessTokenSilently }: Auth0ContextInterface,
    merchantId: number
  ): Promise<string | null> {
    const accessToken = await getAccessTokenSilently();

    try {
      const response = await postRequest<{ api_key: string }>(
        accessToken,
        `merchants/${merchantId}/api-key`,
        {}
      );
      return response.data.api_key;
    } catch (err) {
      return null;
    }
  }

  public static async revokeApiKey(
    { getAccessTokenSilently }: Auth0ContextInterface,
    merchantId: number
  ): Promise<boolean> {
    const accessToken = await getAccessTokenSilently();

    try {
      const response = await deleteRequest<{ api_key: string }>(
        RequestType.DELETE,
        accessToken,
        `merchants/${merchantId}/api-key`,
        {}
      );
      return true;
    } catch (err) {
      return false;
    }
  }

  public static async getMerchantUsage(
    { getAccessTokenSilently }: Auth0ContextInterface,
    merchantId: number
  ): Promise<MerchantUsageDomain> {
    const accessToken = await getAccessTokenSilently();

    const merchantUsage = (
      await request(
        RequestType.GET,
        accessToken,
        `/merchants/${merchantId}/usage`
      )
    ).data;

    return merchantUsageTransformerDtoToDomain(merchantUsage);
  }
}
